import { FC, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import { getRoles, getUserRoles, setRole } from "toolkit/users/users.thunks";
import { Link, useParams } from "react-router-dom";
import { ReactComponent as TopArrow } from "images/top-arrow.svg";
import cn from "classnames";
import styles from "../UserRoles.module.sass";
import { useOutsideAlerter } from "hooks/useOutsideAlerter";

interface DropDownRowProps {
	organization: any;
	admin: boolean;
	userId: string | undefined;
}

const DropDownRow: FC<DropDownRowProps> = ({ organization, admin, userId }) => {
	const dispatch = useAppDispatch();

	const [active, setActive] = useState<boolean>(false);

	const roles = useAppSelector((state) => state.users.roles);

	const localRolesList = useMemo(() => {
		return [...roles.system, ...roles.user];
	}, [roles]);

	useEffect(() => {
		const handleActive = () => {
			setActive(false);
		};

		document.addEventListener("click", handleActive);

		return () => document.removeEventListener("click", handleActive);
	}, []);

	useEffect(() => {
		setActive(false);
	}, [admin, organization]);

	const handleRole = (role: (typeof localRolesList)[0]) => {
		if (role.name === "По умолчанию") {
			dispatch(setRole({ userId, organizationId: organization.id, role }));
		} else {
			dispatch(setRole({ userId, organizationId: organization.id, role }));
		}
		setActive(false);
	};

	const ref = useRef<HTMLDivElement | null>(null);

	useOutsideAlerter(ref.current, () => setActive(false));

	return (
		<Fragment>
			<div ref={ref} className={styles.row}>
				<Link to={`/organization/${organization.id}`} className={styles.rowItem}>
					<span>{organization.name}</span>
				</Link>
				<div
					className={cn(styles.rowItem, styles.dropDown, {
						[styles.active]: active,
					})}
					onClick={(e) => {
						e.stopPropagation();
						!admin && setActive((prevState) => !prevState);
					}}>
					<span>{admin ? "Админ" : organization.role ? organization.role.name : "По умолчанию"}</span>
					<TopArrow className={cn(styles.topArrowIcon, { [styles.activeIcon]: active })} />
					{active && (
						<div className={styles.popup} onClick={(e) => e.stopPropagation()}>
							{localRolesList.map((role) => (
								<div className={styles.roleName} onClick={() => handleRole(role)}>
									{role.name}
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</Fragment>
	);
};

interface UserRolesTableProps {
	query: string;
}

const UserRolesTable: FC<UserRolesTableProps> = ({ query }) => {
	const dispatch = useAppDispatch();

	const { id } = useParams();

	const { admin, organizations } = useAppSelector((state) => state.users.currentUser);

	useEffect(() => {
		if (id) {
			dispatch(getUserRoles({ userId: id, query }));
		}
		//eslint-disable-next-line
	}, [query]);

	useEffect(() => {
		dispatch(getRoles());
		//eslint-disable-next-line
	}, []);

	return (
		<div>
			<div className={styles.tableHead}>
				<div className={styles.tableItem}>Организация</div>
				<div className={styles.tableItem}>Роль в организации</div>
			</div>
			<div className={cn(styles.tableBody, { [styles.admin]: admin })}>
				{organizations?.map((organization) => (
					<DropDownRow organization={organization} admin={admin} key={organization.id} userId={id} />
				))}
			</div>
		</div>
	);
};

export default UserRolesTable;
